import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  XCircle,
  Filter,
  BookOpen,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { CriterionFeedbackCard } from '../components/exam/CriterionFeedbackCard';
import { AttemptQuestionImage } from '../components/exam/AttemptQuestionImage';
import { resultsService } from '../services/results.service';
import apiClient from '../lib/api-client';
import { getAttemptToken } from '../lib/attempt-token';
import { mapApiToReviewQuestions, type ReviewQuestion } from './review-mapping';

const ReviewAudio: React.FC<{ path: string }> = ({ path }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    const attemptId = path.split('/')[3];
    const token = getAttemptToken(attemptId);
    apiClient.get<Blob>(path, {
      responseType: 'blob',
      ...(token ? { headers: { 'x-attempt-token': token } } : {}),
    }).then((res) => {
      if (!cancelled) {
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      }
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  return url
    ? <audio controls src={url} className="w-full mb-6" />
    : <p className="mb-6 text-sm text-gray-500">{t(failed ? 'answerReview.audioUnavailable' : 'answerReview.audioLoading')}</p>;
};

export const AnswerReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const attemptId = searchParams.get('attemptId');
  const program = searchParams.get('program') || 'I';
  const [examType, setExamType] = useState<string | null>(null);
  const section = examType === 'knm' ? 'KNM' : ({ reading: 'Lezen', listening: 'Luisteren', writing: 'Schrijven', speaking: 'Spreken' } as Record<string, string>)[examType || ''] || searchParams.get('section') || 'Lezen';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterMode, setFilterMode] = useState<'all' | 'incorrect' | 'correct'>('all');

  const [loading, setLoading] = useState(!!attemptId);
  const [error, setError] = useState<string | null>(null);
  const [allQuestions, setAllQuestions] = useState<ReviewQuestion[]>([]);
  const [resultPercentage, setResultPercentage] = useState<number | null>(null);

  useEffect(() => {
    if (!attemptId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    resultsService
      .getResultsWithReview(attemptId)
      .then((data) => {
        if (!cancelled) {
          setAllQuestions(mapApiToReviewQuestions(data));
          setResultPercentage(data.percentage);
          setExamType(data.exam.type);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Could not load review data.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [attemptId]);

  const filteredQuestions = useMemo(() => {
    switch (filterMode) {
      case 'incorrect':
        return allQuestions.filter((q) => q.isCorrect === false);
      case 'correct':
        return allQuestions.filter((q) => q.isCorrect === true);
      default:
        return allQuestions;
    }
  }, [allQuestions, filterMode]);

  const current = filteredQuestions[currentIndex];

  const stats = useMemo(() => {
    if (allQuestions.length === 0) return { correct: 0, incorrect: 0, total: 0, score: 0 };
    const correct = allQuestions.filter((q) => q.isCorrect === true).length;
    const incorrect = allQuestions.filter((q) => q.isCorrect === false).length;
    const score = resultPercentage == null ? 0 : Math.round(resultPercentage);
    return { correct, incorrect, total: allQuestions.length, score };
  }, [allQuestions, resultPercentage]);

  const navigateQuestion = (offset: number) => {
    setCurrentIndex((prev) => {
      const next = prev + offset;
      if (next < 0 || next >= filteredQuestions.length) return prev;
      return next;
    });
  };

  const handleReturnToResults = () => {
    if (attemptId) {
      navigate(`/results?attemptId=${attemptId}`);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-error mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')}>{t('profile.backToDashboard')}</Button>
          </div>
        </main>
      </div>
    );
  }

  if (!attemptId || allQuestions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">{t('answerReview.noQuestionsWithFilter')}</p>
            <Button onClick={() => navigate('/dashboard')}>{t('profile.backToDashboard')}</Button>
          </div>
        </main>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="sticky top-0 z-50 bg-white shadow-md">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-gray-900">{t('answerReview.title')}</h1>
              <button
                onClick={handleReturnToResults}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">{t('answerReview.noQuestionsWithFilter')}</p>
            <Button onClick={() => setFilterMode('all')}>{t('answerReview.showAllQuestions')}</Button>
          </div>
        </main>
      </div>
    );
  }

  const isCorrect = current.isCorrect === true;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-teal-50 to-white">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                <Search className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('answerReview.title')}</h1>
                <p className="text-xs text-gray-500">
                  {examType === 'knm' ? section : `${t('exam.common.program')} ${program} · ${section}`}
                </p>
              </div>
            </div>
            <button
              onClick={handleReturnToResults}
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Stats Bar */}
          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="text-success font-medium">{stats.correct} {t('results.quickStats.correct').toLowerCase()}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="w-4 h-4 text-error" />
              <span className="text-error font-medium">{stats.incorrect} {t('results.quickStats.incorrect').toLowerCase()}</span>
            </div>
            <div className="flex-1" />
            <span className="text-sm font-bold text-primary">{stats.score}%</span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Filter Tabs */}
            <div className="bg-white rounded-xl shadow-md p-2 flex gap-2">
              <button
                onClick={() => { setFilterMode('all'); setCurrentIndex(0); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === 'all' ? 'bg-teal-100 text-teal-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Filter className="w-4 h-4 inline mr-1" />
                {t('common.viewAll')} ({stats.total})
              </button>
              <button
                onClick={() => { setFilterMode('incorrect'); setCurrentIndex(0); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === 'incorrect' ? 'bg-error/10 text-error' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <XCircle className="w-4 h-4 inline mr-1" />
                {t('results.quickStats.incorrect')} ({stats.incorrect})
              </button>
              <button
                onClick={() => { setFilterMode('correct'); setCurrentIndex(0); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === 'correct' ? 'bg-success/10 text-success' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <CheckCircle className="w-4 h-4 inline mr-1" />
                {t('results.quickStats.correct')} ({stats.correct})
              </button>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    isCorrect ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                  }`}>
                    {t('exam.common.question')} {current.questionNumber}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                    {current.topic}
                  </span>
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCorrect ? 'bg-success text-white' : 'bg-error text-white'
                }`}>
                  {isCorrect ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                </div>
              </div>

              {/* Context */}
              {current.context && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <BookOpen className="w-4 h-4 text-gray-500 mt-0.5" />
                    <p className="text-sm text-gray-600 italic">{current.context}</p>
                  </div>
                </div>
              )}

              {/* Question */}
              <p className="text-gray-900 font-medium text-lg mb-6">{current.question}</p>
              {attemptId && current.hasImage && <AttemptQuestionImage attemptId={attemptId} questionId={current.id} theme={current.topic} />}

              {attemptId && current.hasAudio && <ReviewAudio path={`/exams/attempts/${attemptId}/listening-audio/${current.id}`} />}
              {attemptId && current.hasRecording && <ReviewAudio path={`/exams/attempts/${attemptId}/audio/${current.id}`} />}
              {current.transcript && <p className="mb-4 text-sm text-gray-700"><strong>{t('answerReview.transcript')}:</strong> {current.transcript}</p>}
              {current.aiScore != null && <p className="mb-4 text-sm text-gray-700"><strong>{t('answerReview.aiScore')}:</strong> {current.aiScore}%</p>}
              {current.aiFeedback && <p className="mb-4 text-sm text-gray-700"><strong>{t('answerReview.feedback')}:</strong> {current.aiFeedback}</p>}
              {current.writtenAnswer && <p className="mb-4 whitespace-pre-wrap text-sm text-gray-700"><strong>{t('answerReview.yourAnswer')}:</strong> {current.writtenAnswer}</p>}
              {current.options.length === 0 && !current.hasRecording && !current.transcript && !current.writtenAnswer && (
                <p className="mb-6 text-sm text-gray-500">{t('answerReview.notAnswered')}</p>
              )}
              {current.options.length > 0 && current.userAnswer == null && <p className="mb-4 text-sm text-gray-500">{t('answerReview.notAnswered')}</p>}

              {/* Options with color coding */}
              <div className="space-y-3 mb-6">
                {current.options.map((opt, idx) => {
                  const isUserAnswer = current.userAnswer !== null && idx === current.userAnswer;
                  const isCorrectAnswer = current.correctAnswer !== null && idx === current.correctAnswer;

                  let borderColor = 'border-gray-200';
                  let bgColor = 'bg-white';
                  let textColor = 'text-gray-700';

                  if (isCorrectAnswer) {
                    borderColor = 'border-success';
                    bgColor = 'bg-success/10';
                    textColor = 'text-success';
                  } else if (isUserAnswer && !isCorrect) {
                    borderColor = 'border-error';
                    bgColor = 'bg-error/10';
                    textColor = 'text-error';
                  }

                  return (
                    <div
                      key={idx}
                      className={`px-4 py-3 rounded-xl border-2 ${borderColor} ${bgColor} transition-all`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          isCorrectAnswer 
                            ? 'bg-success text-white' 
                            : isUserAnswer && !isCorrect 
                            ? 'bg-error text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <div className="flex-1">
                          <p className={`${textColor} ${isCorrectAnswer || isUserAnswer ? 'font-medium' : ''}`}>
                            {opt}
                          </p>
                          <div className="flex gap-2 mt-1">
                            {isUserAnswer && (
                              <span className={`text-xs font-medium ${isCorrect ? 'text-success' : 'text-error'}`}>
                                ← {t('answerReview.yourAnswer')}
                              </span>
                            )}
                            {isCorrectAnswer && !isUserAnswer && (
                              <span className="text-xs font-medium text-success">
                                ← {t('answerReview.correct')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Explanation */}
              <div className={`rounded-xl p-5 ${isCorrect ? 'bg-success/10 border border-success/20' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCorrect ? 'bg-success/20' : 'bg-amber-100'}`}>
                    <Lightbulb className={`w-5 h-5 ${isCorrect ? 'text-success' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-bold mb-1 ${isCorrect ? 'text-success' : 'text-amber-800'}`}>
                      {t('answerReview.explanation')}
                    </p>
                    <p className={`text-sm ${isCorrect ? 'text-success/90' : 'text-amber-700'}`}>
                      {current.explanation || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Criterion Feedback for Speaking Exams */}
              {(current.topic === 'speaking' || current.topic === 'essay') && current.criteriaScores && current.criteriaScores.length > 0 && (
                <CriterionFeedbackCard
                  criteria={current.criteriaScores}
                  questionNumber={current.questionNumber}
                  title={t('answerReview.criteria')}
                />
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="tertiary"
                onClick={() => navigateQuestion(-1)}
                disabled={currentIndex === 0}
                icon={<ChevronLeft className="w-4 h-4" />}
              >
                {t('answerReview.previous')}
              </Button>

              <span className="text-sm text-gray-600">
                {currentIndex + 1} / {filteredQuestions.length}
              </span>

              <Button
                variant="tertiary"
                onClick={() => navigateQuestion(1)}
                disabled={currentIndex === filteredQuestions.length - 1}
                className="flex-row-reverse"
                icon={<ChevronRight className="w-4 h-4" />}
              >
                {t('answerReview.next')}
              </Button>
            </div>
          </div>

          {/* Sidebar - Question Navigator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 sticky top-32">
              <h3 className="font-bold text-gray-900 mb-3">{t('exam.common.questionNavigator')}</h3>

              <div className="grid grid-cols-4 lg:grid-cols-3 gap-2 mb-4">
                {filteredQuestions.map((q, idx) => {
                  const qIsCorrect = q.isCorrect === true;
                  const isCurrent = idx === currentIndex;

                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-full aspect-square rounded-lg text-sm font-medium flex items-center justify-center transition-all ${
                        isCurrent
                          ? 'ring-2 ring-teal-500 ring-offset-2'
                          : ''
                      } ${
                        qIsCorrect
                          ? 'bg-success/20 text-success'
                          : 'bg-error/20 text-error'
                      }`}
                    >
                      {q.questionNumber}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="space-y-2 text-xs text-gray-600 mb-4 border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-success/20" />
                  <span>{t('answerReview.correctlyAnswered')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-error/20" />
                  <span>{t('answerReview.incorrectlyAnswered')}</span>
                </div>
              </div>

              <Button
                variant="primary"
                className="w-full"
                onClick={handleReturnToResults}
              >
                {t('answerReview.returnToResults')}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnswerReviewPage;
